import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { Save, MessageCircle, Send, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
      {label}
    </label>
    {children}
    {hint ? <div className="mt-1 text-[11px] text-gray-500">{hint}</div> : null}
  </div>
);

// Provider presets — auto-fill api_url + show provider-specific guidance.
// Keys are matched case-insensitively in the backend send_sms_async.
const PROVIDER_PRESETS = {
  Textbelt: {
    api_url: "https://textbelt.com/text",
    api_key_hint: 'Use "textbelt" for the free 1-SMS-per-day quota, or paste a paid Textbelt key.',
    sender_hint: "Optional — most carriers ignore custom sender for short codes.",
    free: true,
    notes: "Free tier: 1 SMS / IP / day. Useful to verify wiring without signing up.",
  },
  "sms.net.bd": {
    api_url: "https://api.sms.net.bd/sendsms",
    api_key_hint: "Get from sms.net.bd dashboard → API.",
    sender_hint: "Approved Sender ID from sms.net.bd (e.g. ScriptShop).",
    free: false,
    notes: "Bangladesh provider. ~5 paisa/SMS. Backend parses error=0 as success.",
  },
  BulkSMSBD: {
    api_url: "https://api.sms.net.bd/sendsms",
    api_key_hint: "BulkSMSBD/sms.net.bd shared API.",
    sender_hint: "Approved Sender ID.",
    free: false,
    notes: "Same backend handler as sms.net.bd.",
  },
  Twilio: {
    api_url: "",
    api_key_hint: 'Format: "ACCOUNT_SID:AUTH_TOKEN"  (e.g. "ACxxxx:abcd1234")',
    sender_hint: 'Your Twilio sender number (e.g. "+12025551234").',
    free: false,
    notes: "Twilio Programmable SMS REST API.",
  },
  "SSL Wireless": {
    api_url: "",
    api_key_hint: "Provided by SSL Wireless after onboarding.",
    sender_hint: "Approved alpha sender ID.",
    free: false,
    notes: "Generic POST handler — may need a custom integration on backend if format differs.",
  },
  Custom: {
    api_url: "",
    api_key_hint: "Generic provider — backend posts {api_key, msg, to, sender_id} as form-data.",
    sender_hint: "Optional sender id.",
    free: false,
    notes: "Best-effort generic handler. HTTP 200 = success.",
  },
};

export default function SMSGateway() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/settings/sms");
      setForm(data);
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onProviderChange = (newProvider) => {
    const preset = PROVIDER_PRESETS[newProvider];
    setForm((f) => ({
      ...f,
      provider: newProvider,
      // Only auto-fill api_url if it was empty or matched another preset
      api_url:
        !f.api_url ||
        Object.values(PROVIDER_PRESETS).some((p) => p.api_url && p.api_url === f.api_url)
          ? preset?.api_url || ""
          : f.api_url,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.put("/settings/sms", form);
      setForm(data);
      toast.success("SMS gateway saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) {
      toast.error("Enter a destination phone number (with country code, e.g. +88017XXXXXXXX)");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      // Persist current form first so we test the live config
      await api.put("/settings/sms", form);
      const { data } = await api.post("/settings/sms/test", {
        phone: testPhone.trim(),
        message: `Maxlive88 SMS gateway test ✓ ${new Date().toLocaleTimeString()}`,
      });
      setTestResult(data);
      if (data.ok) toast.success("SMS sent — check the destination handset");
      else toast.error("SMS failed — see details below");
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setTestResult({ ok: false, detail });
      toast.error(`Test failed: ${detail}`);
    } finally {
      setTesting(false);
    }
  };

  if (!form) {
    return (
      <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">
        Loading...
      </div>
    );
  }

  const preset = PROVIDER_PRESETS[form.provider] || PROVIDER_PRESETS.Custom;

  return (
    <div data-testid="sms-gateway-page" className="space-y-5">
      <PageHeader
        title="SMS Gateway"
        subtitle="Configure the SMS provider used for OTPs and transactional alerts"
      />

      <form
        onSubmit={submit}
        className="max-w-3xl bg-white border border-gray-200 rounded-sm p-6 space-y-5"
        data-testid="sms-form"
      >
        <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
          <div className="h-10 w-10 bg-blue-50 text-blue-600 border border-blue-200 rounded-sm flex items-center justify-center">
            <MessageCircle size={18} />
          </div>
          <div>
            <div className="font-display text-lg font-bold">SMS Provider</div>
            <div className="text-xs text-gray-500">
              Used for user verification, login OTP and deposit/withdraw alerts.
            </div>
          </div>
          <div className="ml-auto">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => set("enabled", e.target.checked)}
                data-testid="sms-enabled"
              />
              <span className="text-xs font-semibold uppercase tracking-wider">
                {form.enabled ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>
        </div>

        {preset?.notes && (
          <div className="bg-blue-50 border border-blue-200 rounded-sm px-3 py-2 text-xs text-blue-900 flex items-start gap-2">
            <span className="font-semibold uppercase tracking-wider text-[10px] bg-blue-100 px-1.5 py-0.5 rounded">
              {preset.free ? "FREE" : "Paid"}
            </span>
            <span>{preset.notes}</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Provider">
            <select
              value={form.provider}
              onChange={(e) => onProviderChange(e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
              data-testid="sms-provider"
            >
              {Object.keys(PROVIDER_PRESETS).map((p) => (
                <option key={p}>{p}</option>
              ))}
            </select>
          </Field>
          <Field label="Sender ID" hint={preset?.sender_hint}>
            <input
              value={form.sender_id || ""}
              onChange={(e) => set("sender_id", e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
              data-testid="sms-sender"
            />
          </Field>
          <div className="md:col-span-2">
            <Field label="API URL">
              <input
                value={form.api_url || ""}
                onChange={(e) => set("api_url", e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono"
                data-testid="sms-api-url"
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="API Key" hint={preset?.api_key_hint}>
              <input
                type="text"
                value={form.api_key || ""}
                onChange={(e) => set("api_key", e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono"
                placeholder={form.provider === "Textbelt" ? 'textbelt' : "Enter your provider API key"}
                data-testid="sms-api-key"
              />
            </Field>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-100">
          <button
            type="submit"
            disabled={loading}
            className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="sms-save"
          >
            <Save size={14} /> {loading ? "Saving..." : "Save SMS Gateway"}
          </button>
        </div>
      </form>

      {/* ---------- Test send panel ---------- */}
      <div className="max-w-3xl bg-white border border-gray-200 rounded-sm p-6 space-y-4" data-testid="sms-test-panel">
        <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
          <div className="h-10 w-10 bg-amber-50 text-amber-600 border border-amber-200 rounded-sm flex items-center justify-center">
            <Send size={18} />
          </div>
          <div>
            <div className="font-display text-lg font-bold">Send a Test SMS</div>
            <div className="text-xs text-gray-500">
              Save the gateway above first, then send a real SMS to verify the provider works end-to-end.
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="md:col-span-2">
            <Field label="Destination phone (with country code)">
              <input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="+88017XXXXXXXX"
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono"
                data-testid="sms-test-phone"
              />
            </Field>
          </div>
          <button
            type="button"
            onClick={sendTest}
            disabled={testing || !form.enabled}
            className="h-10 px-5 bg-amber-500 hover:bg-amber-600 text-white rounded-sm text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="sms-test-send"
            title={!form.enabled ? "Enable the gateway first" : "Send test SMS"}
          >
            {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {testing ? "Sending..." : "Send test SMS"}
          </button>
        </div>

        {!form.enabled && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
            Enable the gateway above and save before testing.
          </div>
        )}

        {testResult && (
          <div
            className={`rounded-sm px-4 py-3 border text-sm flex items-start gap-3 ${
              testResult.ok
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-red-50 border-red-200 text-red-900"
            }`}
            data-testid="sms-test-result"
          >
            {testResult.ok ? (
              <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 shrink-0" />
            ) : (
              <XCircle size={18} className="text-red-600 mt-0.5 shrink-0" />
            )}
            <div className="space-y-1 min-w-0">
              <div className="font-semibold">
                {testResult.ok ? "SMS sent successfully" : "SMS failed"}
              </div>
              <div className="text-xs font-mono break-words whitespace-pre-wrap">{testResult.detail || ""}</div>
              {testResult.phone && <div className="text-xs">To: {testResult.phone}</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
